import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Zap, Plus, Trash2, Loader2, Gauge } from "lucide-react";
import { API, authHeaders } from "./shared";

const field = "w-full px-3 py-2 rounded-md bg-black border border-white/15 text-white text-sm focus:border-[#F2B705] outline-none [color-scheme:dark]";

const RULE_TYPES = [
  { v: "weekend", l: "Weekend Pricing", hint: "Adjust prices on Fri/Sat/Sun rental days." },
  { v: "holiday", l: "Holiday Pricing", hint: "Adjust prices for a specific date window (e.g. July 4th weekend)." },
  { v: "high_demand", l: "High-Demand Pricing", hint: "Kicks in automatically when fleet utilization crosses a threshold." },
  { v: "long_term", l: "Long-Term Discount", hint: "Discount rentals at or above a minimum number of days." },
  { v: "last_minute", l: "Last-Minute Special", hint: "Adjust price when pickup is within X days of booking." },
  { v: "vehicle_promo", l: "Vehicle Promotion", hint: "Promo for one specific vehicle (optional date window)." },
];
const ADJ_TYPES = [
  { v: "percent", l: "% of base rental" },
  { v: "flat", l: "Flat amount (once)" },
  { v: "flat_per_day", l: "Flat amount per day" },
];
const DOWS = [["Mon", 0], ["Tue", 1], ["Wed", 2], ["Thu", 3], ["Fri", 4], ["Sat", 5], ["Sun", 6]];
const EMPTY = { name: "", rule_type: "weekend", adj_type: "percent", value: "", params: { days_of_week: [4, 5, 6] } };

export default function DynamicPricingModule({ token }) {
  const H = authHeaders(token);
  const [data, setData] = useState(null);
  const [vehicles, setVehicles] = useState([]);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  const load = () => axios.get(`${API}/pricing/rules`, H).then((r) => setData(r.data)).catch(() => {});
  useEffect(() => { load(); axios.get(`${API}/vehicles`).then((r) => setVehicles(r.data || [])); /* eslint-disable-next-line */ }, [token]);

  const setP = (k, v) => setForm((f) => ({ ...f, params: { ...f.params, [k]: v } }));
  const changeType = (t) => setForm((f) => ({ ...f, rule_type: t, params: t === "weekend" ? { days_of_week: [4, 5, 6] } : {} }));

  const create = async () => {
    if (!form.name.trim()) return toast.error("Give the rule a name.");
    if (!Number(form.value)) return toast.error("Enter an adjustment value (negative = discount).");
    setSaving(true);
    try {
      await axios.post(`${API}/pricing/rules`, { ...form, value: Number(form.value), enabled: false }, H);
      toast.success("Rule created — flip it ON when you're ready.");
      setForm(EMPTY);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not create rule."); }
    finally { setSaving(false); }
  };

  const toggle = async (r) => {
    try {
      await axios.patch(`${API}/pricing/rules/${r.id}`, { enabled: !r.enabled }, H);
      toast.success(r.enabled ? `"${r.name}" turned OFF.` : `"${r.name}" is now LIVE on quotes.`);
      load();
    } catch { toast.error("Could not update rule."); }
  };

  const remove = async (r) => {
    if (!window.confirm(`Delete "${r.name}"?`)) return;
    try { await axios.delete(`${API}/pricing/rules/${r.id}`, H); load(); } catch { toast.error("Could not delete."); }
  };

  const ruleDesc = (r) => {
    const p = r.params || {};
    const adj = r.adj_type === "percent" ? `${r.value > 0 ? "+" : ""}${r.value}% of base`
      : r.adj_type === "flat_per_day" ? `${r.value > 0 ? "+" : "-"}$${Math.abs(r.value)}/day` : `${r.value > 0 ? "+" : "-"}$${Math.abs(r.value)} flat`;
    const cond = {
      weekend: `on ${(p.days_of_week || [4, 5, 6]).map((d) => DOWS.find(([, v]) => v === d)?.[0]).join("/")}`,
      holiday: `${p.start_date || "?"} → ${p.end_date || "?"}`,
      high_demand: `when utilization ≥ ${p.threshold_pct || 80}%`,
      long_term: `rentals of ${p.min_days || 14}+ days`,
      last_minute: `pickup within ${p.within_days ?? 1} day(s)`,
      vehicle_promo: vehicles.find((v) => v.id === p.vehicle_id) ? `${vehicles.find((v) => v.id === p.vehicle_id).year} ${vehicles.find((v) => v.id === p.vehicle_id).make} ${vehicles.find((v) => v.id === p.vehicle_id).model}` : "specific vehicle",
    }[r.rule_type] || "";
    return `${adj} · ${cond}`;
  };

  if (!data) return <div className="flex items-center gap-2 text-white/50 py-16 justify-center"><Loader2 className="animate-spin" size={18} /> Loading pricing rules…</div>;

  return (
    <div className="space-y-5" data-testid="dynamic-pricing-module">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h3 className="font-display font-bold text-white flex items-center gap-2"><Zap size={17} className="text-[#F2B705]" /> Dynamic Pricing Engine</h3>
          <p className="text-white/45 text-sm mt-0.5">Rules adjust live quotes automatically. Negative value = discount, positive = surcharge. Premium Package vehicles are never affected.</p>
        </div>
        <span data-testid="dp-utilization" className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full border border-white/15 text-white/60"><Gauge size={13} className="text-[#F2B705]" /> Fleet utilization now: <b className="text-white">{data.utilization}%</b></span>
      </div>

      {/* Rules list */}
      <div className="space-y-2" data-testid="dp-rules-list">
        {data.rules.length === 0 && <p className="text-white/40 text-sm bg-[#0C0C0C] border border-white/10 rounded-xl p-5 text-center">No pricing rules yet — create your first one below. Rules start OFF, so nothing changes until you enable them.</p>}
        {data.rules.map((r) => (
          <div key={r.id} data-testid={`dp-rule-${r.id}`} className={`flex items-center gap-3 bg-[#0C0C0C] border rounded-xl px-4 py-3 ${r.enabled ? "border-[#F2B705]/40" : "border-white/10"}`}>
            <button data-testid={`dp-toggle-${r.id}`} onClick={() => toggle(r)}
              className={`relative w-11 h-6 rounded-full transition-colors shrink-0 ${r.enabled ? "bg-[#F2B705]" : "bg-white/15"}`}>
              <span className={`absolute top-0.5 h-5 w-5 rounded-full bg-black transition-all ${r.enabled ? "left-[22px]" : "left-0.5"}`} />
            </button>
            <div className="min-w-0 mr-auto">
              <div className="text-white font-semibold text-sm flex items-center gap-2">
                {r.name}
                <span className="text-[10px] uppercase px-1.5 py-0.5 rounded bg-white/5 text-white/50">{RULE_TYPES.find((t) => t.v === r.rule_type)?.l || r.rule_type}</span>
                {r.enabled && <span className="text-[10px] uppercase px-1.5 py-0.5 rounded bg-[#F2B705]/15 text-[#F2B705] font-bold">LIVE</span>}
              </div>
              <div className={`text-xs mt-0.5 ${r.value < 0 ? "text-emerald-400/80" : "text-amber-400/80"}`}>{ruleDesc(r)}</div>
            </div>
            <button data-testid={`dp-delete-${r.id}`} onClick={() => remove(r)} className="text-red-400/60 hover:text-red-400 shrink-0"><Trash2 size={15} /></button>
          </div>
        ))}
      </div>

      {/* Create rule */}
      <div className="bg-[#0C0C0C] border border-white/10 rounded-xl p-5" data-testid="dp-create-form">
        <h4 className="text-sm font-semibold text-white/80 mb-3 flex items-center gap-2"><Plus size={15} className="text-[#F2B705]" /> New Pricing Rule</h4>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
          <div>
            <label className="text-[11px] text-white/45 block mb-1">Rule Name</label>
            <input data-testid="dp-name-input" className={field} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="e.g. Weekend Bump" />
          </div>
          <div>
            <label className="text-[11px] text-white/45 block mb-1">Rule Type</label>
            <select data-testid="dp-type-select" className={field} value={form.rule_type} onChange={(e) => changeType(e.target.value)}>
              {RULE_TYPES.map((t) => <option key={t.v} value={t.v}>{t.l}</option>)}
            </select>
          </div>
          <div>
            <label className="text-[11px] text-white/45 block mb-1">Adjustment</label>
            <select data-testid="dp-adj-select" className={field} value={form.adj_type} onChange={(e) => setForm({ ...form, adj_type: e.target.value })}>
              {ADJ_TYPES.map((t) => <option key={t.v} value={t.v}>{t.l}</option>)}
            </select>
          </div>
          <div>
            <label className="text-[11px] text-white/45 block mb-1">Value (negative = discount)</label>
            <input data-testid="dp-value-input" type="number" className={field} value={form.value} onChange={(e) => setForm({ ...form, value: e.target.value })} placeholder={form.adj_type === "percent" ? "e.g. 15 or -10" : "e.g. 20 or -25"} />
          </div>
        </div>
        <p className="text-white/35 text-xs mt-2">{RULE_TYPES.find((t) => t.v === form.rule_type)?.hint}</p>

        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3 mt-3">
          {form.rule_type === "weekend" && (
            <div className="lg:col-span-2">
              <label className="text-[11px] text-white/45 block mb-1">Applies On</label>
              <div className="flex gap-1.5 flex-wrap">
                {DOWS.map(([l, v]) => {
                  const on = (form.params.days_of_week || []).includes(v);
                  return (
                    <button key={v} data-testid={`dp-dow-${l}`} onClick={() => setP("days_of_week", on ? form.params.days_of_week.filter((d) => d !== v) : [...(form.params.days_of_week || []), v])}
                      className={`px-2.5 py-1.5 rounded-md text-xs font-semibold ${on ? "bg-[#F2B705] text-black" : "bg-white/5 border border-white/10 text-white/50"}`}>{l}</button>
                  );
                })}
              </div>
            </div>
          )}
          {(form.rule_type === "holiday" || form.rule_type === "vehicle_promo") && (
            <>
              <div>
                <label className="text-[11px] text-white/45 block mb-1">Start Date{form.rule_type === "vehicle_promo" ? " (optional)" : ""}</label>
                <input data-testid="dp-start-input" type="date" className={field} value={form.params.start_date || ""} onChange={(e) => setP("start_date", e.target.value)} />
              </div>
              <div>
                <label className="text-[11px] text-white/45 block mb-1">End Date{form.rule_type === "vehicle_promo" ? " (optional)" : ""}</label>
                <input data-testid="dp-end-input" type="date" className={field} value={form.params.end_date || ""} onChange={(e) => setP("end_date", e.target.value)} />
              </div>
            </>
          )}
          {form.rule_type === "high_demand" && (
            <div>
              <label className="text-[11px] text-white/45 block mb-1">Utilization Threshold %</label>
              <input data-testid="dp-threshold-input" type="number" className={field} value={form.params.threshold_pct || ""} onChange={(e) => setP("threshold_pct", Number(e.target.value))} placeholder="80" />
            </div>
          )}
          {form.rule_type === "long_term" && (
            <div>
              <label className="text-[11px] text-white/45 block mb-1">Minimum Days</label>
              <input data-testid="dp-mindays-input" type="number" className={field} value={form.params.min_days || ""} onChange={(e) => setP("min_days", Number(e.target.value))} placeholder="14" />
            </div>
          )}
          {form.rule_type === "last_minute" && (
            <div>
              <label className="text-[11px] text-white/45 block mb-1">Pickup Within (days)</label>
              <input data-testid="dp-within-input" type="number" className={field} value={form.params.within_days ?? ""} onChange={(e) => setP("within_days", Number(e.target.value))} placeholder="1" />
            </div>
          )}
          {form.rule_type === "vehicle_promo" && (
            <div>
              <label className="text-[11px] text-white/45 block mb-1">Vehicle</label>
              <select data-testid="dp-vehicle-select" className={field} value={form.params.vehicle_id || ""} onChange={(e) => setP("vehicle_id", e.target.value)}>
                <option value="">Choose a vehicle…</option>
                {vehicles.map((v) => <option key={v.id} value={v.id}>{v.year} {v.make} {v.model}</option>)}
              </select>
            </div>
          )}
        </div>

        <button data-testid="dp-create-btn" onClick={create} disabled={saving}
          className="mt-4 flex items-center gap-2 px-5 py-2.5 rounded-md bg-[#F2B705] text-black font-bold text-sm hover:bg-white transition-colors disabled:opacity-60">
          {saving ? <Loader2 className="animate-spin" size={14} /> : <Plus size={14} />} Create Rule (starts OFF)
        </button>
      </div>
    </div>
  );
}
